import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { 
  Plane, User, Mail, Phone, Calendar, CreditCard, Lock,
  ArrowRight, Check, Loader2, AlertCircle, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { flights as flightsApi } from '../lib/api';
import api from '../lib/api';
import PaymentBrandLogos from '../components/payment/PaymentBrandLogos';
import {
  expiryMonthOptions,
  expiryYearOptions,
  formatCardNumberInput,
  buildFlightPaymentCardPayload,
  buildZiraatCardPayloadFromForm,
  validatePaymentCard,
} from '../lib/paymentCardForm';
import { extractFlightBookingError } from '../lib/flightBookingErrors';
import { useCurrency } from '../lib/currency-context';
import FlightCancellationPolicy from '../components/flights/FlightCancellationPolicy';
import FlightPrice, { FlightPriceInline } from '../components/flights/FlightPrice';
import { formatFlightConnectionLabel } from '../lib/flightConnection';

const FlightBookingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatPrice } = useCurrency();
  
  /** Ziraat dönüşü veya API hatası — üst banner */
  const [paymentNotice, setPaymentNotice] = useState(null);
  
  // Selected flight from session
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  /** Backend /price ile güncel tutar (arama sonucu yerine; yolcu sayısı + marj uyumu) */
  const [pricedQuote, setPricedQuote] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  /** Rezervasyon hatası — toast kaybolsa da sayfada kalır */
  const [bookingError, setBookingError] = useState(null);
  
  /** ziraat (3D) veya airtuerk_cc (doğrudan MOTO) */
  const [paymentMode, setPaymentMode] = useState('ziraat');
  
  // Passenger forms
  const [passengers, setPassengers] = useState([]);
  
  // Contact info
  const [contact, setContact] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    phone_country: '+90',
    phone_city: ''
  });

  const [paymentCard, setPaymentCard] = useState({
    card_number: '',
    expire_month: '',
    expire_year: '',
    cvc: '',
    card_holder: '',
  });

  useEffect(() => {
    flightsApi.getPaymentConfig()
      .then((res) => {
        if (res.data?.payment_mode) setPaymentMode(res.data.payment_mode);
      })
      .catch(() => {});
  }, []);

  // Ziraat 3D fail → ?error=...&order_id=FLT...
  useEffect(() => {
    const errRaw = searchParams.get('error');
    const orderId =
      searchParams.get('order_id') ||
      sessionStorage.getItem('pendingFlightBookingId') ||
      null;
    const failed = searchParams.get('payment_failed') === '1' || Boolean(errRaw);

    if (!failed && !errRaw) return;

    const message = errRaw ? decodeURIComponent(errRaw.replace(/\+/g, ' ')) : 'Ödeme tamamlanamadı';
    setPaymentNotice({
      type: 'failed',
      message,
      orderId,
    });
    setBookingError(message);
    toast.error(message, { duration: 12000 });

    const next = new URLSearchParams(searchParams);
    next.delete('error');
    next.delete('order_id');
    next.delete('payment_failed');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const cardYearOptions = expiryYearOptions();
  const cardMonthOptions = expiryMonthOptions();

  const handlePaymentCardChange = (field, value) => {
    if (field === 'card_number') {
      value = formatCardNumberInput(value);
    }
    if (field === 'cvc') {
      value = value.replace(/\D/g, '').slice(0, 4);
    }
    setPaymentCard((prev) => ({ ...prev, [field]: value }));
  };
  
  // Date locale
  const getDateLocale = () => {
    switch(i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };
  
  // Load selected flight from session
  useEffect(() => {
    const stored = sessionStorage.getItem('selectedFlight');
    if (stored) {
      const data = JSON.parse(stored);
      setSelectedFlight(data);
      
      // Initialize passenger forms
      const paxCount = data.passengers || { adult: 1, child: 0, infant: 0 };
      const passengerForms = [];
      
      for (let i = 0; i < (paxCount.adult || 1); i++) {
        passengerForms.push({
          pax_type: 'ADULT',
          gender: 'MALE',
          name: '',
          surname: '',
          birthdate: '',
          nationality: 'TR',
          identity_number: '',
          passport_number: '',
          passport_expire_date: ''
        });
      }
      
      for (let i = 0; i < (paxCount.child || 0); i++) {
        passengerForms.push({
          pax_type: 'CHILD',
          gender: 'MALE',
          name: '',
          surname: '',
          birthdate: '',
          nationality: 'TR',
          identity_number: '',
          passport_number: '',
          passport_expire_date: ''
        });
      }
      
      for (let i = 0; i < (paxCount.infant || 0); i++) {
        passengerForms.push({
          pax_type: 'INFANT',
          gender: 'MALE',
          name: '',
          surname: '',
          birthdate: '',
          nationality: 'TR',
          identity_number: '',
          passport_number: '',
          passport_expire_date: ''
        });
      }
      
      setPassengers(passengerForms);
    } else {
      navigate(`/${i18n.language}/flights`);
    }
  }, [navigate, i18n.language]);

  // Güncel fiyat (Airtuerk: kayıtlı arama + yolcu sayısı ile /price)
  useEffect(() => {
    if (!selectedFlight?.searchUUID || !selectedFlight?.offer?.offer_id) return;
    let cancelled = false;
    setPriceLoading(true);
    setPricedQuote(null);
    (async () => {
      try {
        const res = await flightsApi.getPrice({
          search_uuid: selectedFlight.searchUUID,
          selected_offer_id: selectedFlight.offer.offer_id,
          selected_ssr_offer_ids: [],
        });
        if (cancelled) return;
        const tw = res.data?.total_with_fee ?? res.data?.total_price;
        const hf = res.data?.handling_fee;
        const base =
          res.data?.base_price ??
          res.data?.Totals?.GrandTotal?.Total ??
          (tw != null && hf != null ? tw - hf : null);
        if (tw != null) {
          setPricedQuote({
            total_with_fee: tw,
            handling_fee: hf ?? 0,
            base_price: base,
            brand_name: res.data?.brand_name,
            baggage_label: res.data?.baggage_label,
            features: res.data?.features,
          });
        }
      } catch (e) {
        console.warn('Flight price refresh failed', e);
      } finally {
        if (!cancelled) setPriceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFlight]);
  
  // Update passenger field
  const updatePassenger = (index, field, value) => {
    setPassengers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  
  const passengerAgeYears = (birthdate) => {
    if (!birthdate) return null;
    const b = new Date(birthdate);
    if (Number.isNaN(b.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - b.getFullYear();
    const m = today.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age -= 1;
    return age;
  };

  const expectedPaxTypeForAge = (age) => {
    if (age < 2) return 'INFANT';
    if (age < 12) return 'CHILD';
    return 'ADULT';
  };

  const paxTypeLabel = (type) => (
    type === 'ADULT' ? 'Yetişkin' : type === 'CHILD' ? 'Çocuk' : 'Bebek'
  );

  // Validate form
  const validateForm = () => {
    // Validate contact
    if (!contact.name || !contact.surname || !contact.email || !contact.phone) {
      toast.error('Lütfen iletişim bilgilerini doldurun');
      return false;
    }

    const cardErr = validatePaymentCard({
      ...paymentCard,
      full_name: paymentCard.card_holder,
    });
    if (cardErr) {
      toast.error(cardErr);
      return false;
    }
    if (paymentMode === 'airtuerk_cc') {
      const p0 = passengers[0];
      if (p0?.name && p0?.surname) {
        const expected = `${p0.name} ${p0.surname}`.trim().toUpperCase().replace(/\s+/g, ' ');
        const onCard = (paymentCard.card_holder || expected).trim().toUpperCase().replace(/\s+/g, ' ');
        if (onCard !== expected) {
          toast.error(`Kart üzerindeki isim 1. yolcu ile aynı olmalı: ${expected}`);
          return false;
        }
      }
    }
    const phoneDigits = String(contact.phone || '').replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      toast.error('Geçerli bir telefon numarası girin (en az 10 rakam)');
      return false;
    }
    
    // Validate passengers
    for (let i = 0; i < passengers.length; i++) {
      const pax = passengers[i];
      if (!pax.name || !pax.surname || !pax.birthdate) {
        toast.error(`Lütfen ${i + 1}. yolcunun bilgilerini doldurun`);
        return false;
      }

      const age = passengerAgeYears(pax.birthdate);
      if (age !== null) {
        const expected = expectedPaxTypeForAge(age);
        if (pax.pax_type !== expected) {
          toast.error(
            `Yolcu ${i + 1}: ${age} yaş için "${paxTypeLabel(expected)}" seçilmeli (şu an ${paxTypeLabel(pax.pax_type)})`
          );
          return false;
        }
      }
      
      // For international flights, passport is required
      if (!pax.identity_number && !pax.passport_number) {
        toast.error(`Lütfen ${i + 1}. yolcunun TC Kimlik veya Pasaport numarasını girin`);
        return false;
      }
    }
    
    return true;
  };
  
  const buildBookingPayload = (phoneDigits) => {
    const flight = selectedFlight?.flight || selectedFlight;
    const segs = flight?.segments || [];
    const firstSeg = segs[0] || {};
    const lastSeg = segs[segs.length - 1] || firstSeg;

    return {
      search_uuid: selectedFlight.searchUUID,
      selected_offer_id: selectedFlight.offer.offer_id,
      selected_ssr_offer_ids: [],
      passengers: passengers.map(p => ({
        pax_type: p.pax_type,
        gender: p.gender,
        name: p.name.trim().toUpperCase(),
        surname: p.surname.trim().toUpperCase(),
        birthdate: p.birthdate,
        nationality: p.nationality,
        identity_number: p.identity_number || null,
        passport_number: p.passport_number || null,
        passport_expire_date: p.passport_expire_date || null
      })),
      contact: {
        name: contact.name.trim().toUpperCase(),
        surname: contact.surname.trim().toUpperCase(),
        phone_country: contact.phone_country,
        phone_city: phoneDigits.slice(0, 3),
        phone_number: phoneDigits.slice(3),
        email: contact.email
      },
      trx_type: 'TICKET',
      flight_summary: {
        airline_code: flight?.airline_code,
        departure_time: flight?.departure_time,
        arrival_time: flight?.arrival_time,
        origin: firstSeg?.DepartureAirport || firstSeg?.departure_airport || selectedFlight?.route?.origin || flight?.origin,
        destination: lastSeg?.ArrivalAirport || lastSeg?.arrival_airport || selectedFlight?.route?.destination || flight?.destination,
        brand_name: pricedQuote?.brand_name || selectedFlight?.offer?.brand_name || flight?.brand_name,
        stop_count: flight?.stop_count ?? 0,
        baggage_label: pricedQuote?.baggage_label || selectedFlight?.offer?.baggage_label || flight?.baggage_label,
        air_fare_code: selectedFlight?.offer?.air_fare_code,
        features: pricedQuote?.features || selectedFlight?.offer?.features || [],
      },
    };
  };

  // Handle booking
  const handleBooking = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    setBookingError(null);
    
    try {
      const phoneDigits = String(contact.phone || '').replace(/\D/g, '');
      const bookingRequest = buildBookingPayload(phoneDigits);

      if (paymentMode === 'ziraat') {
        const prepareRes = await flightsApi.prepare(bookingRequest);
        const prep = prepareRes.data;
        if (!prep?.booking_id) {
          throw new Error('Rezervasyon hazırlanamadı');
        }

        const totalTry = Number(prep.total_try);
        if (!totalTry || totalTry <= 0) {
          throw new Error('Ödeme tutarı hesaplanamadı');
        }

        const ziraatCard = buildZiraatCardPayloadFromForm({
          ...paymentCard,
          full_name: paymentCard.card_holder,
        });

        sessionStorage.setItem('pendingFlightBookingId', prep.booking_id);

        const paymentResponse = await api.post('/ziraat/initiate', {
          order_id: prep.booking_id,
          ...ziraatCard,
          amount: totalTry,
          currency: 'TRY',
          installment: 1,
          customer_email: contact.email,
          payment_type: 'flight',
        });

        if (paymentResponse.data.success && paymentResponse.data.html_form) {
          document.open();
          document.write(paymentResponse.data.html_form);
          document.close();
          return;
        }
        throw new Error(paymentResponse.data.message || '3D Secure başlatılamadı');
      }

      const cardPayload = buildFlightPaymentCardPayload({
        ...paymentCard,
        full_name: paymentCard.card_holder,
      });
      const response = await flightsApi.book({
        ...bookingRequest,
        payment_card: cardPayload,
      });
      
      setBookingResult(response.data);
      setBookingComplete(true);
      sessionStorage.removeItem('selectedFlight');
      toast.success('Rezervasyon başarıyla oluşturuldu!');
      
    } catch (error) {
      console.error('Booking error:', error);
      const message = extractFlightBookingError(error);
      setBookingError(message);
      toast.error(message, { duration: 12000 });
      requestAnimationFrame(() => {
        document.getElementById('flight-booking-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'HH:mm');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'd MMM yyyy', { locale: getDateLocale?.() });
  };
  
  if (!selectedFlight) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }
  
  // Booking complete view
  if (bookingComplete && bookingResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="text-center">
            <CardContent className="py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-green-800 mb-2">
                Rezervasyon Başarılı!
              </h1>
              
              <p className="text-gray-600 mb-6">
                Ödemeniz alındı ve rezervasyonunuz tamamlandı. PNR kodunuz aşağıdadır.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left space-y-4">
                {selectedFlight?.flight && (
                  <div className="pb-4 border-b border-gray-200">
                    <div className="text-sm text-gray-500 mb-1">Uçuş</div>
                    <div className="font-semibold text-gray-900">
                      {selectedFlight.flight.airline_code} ·{' '}
                      {formatTime(selectedFlight.flight.departure_time)} →{' '}
                      {formatTime(selectedFlight.flight.arrival_time)}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {formatDate(selectedFlight.flight.departure_time)}
                      {selectedFlight.offer?.brand_name
                        ? ` · ${selectedFlight.offer.brand_name}`
                        : ''}
                      {selectedFlight.offer?.baggage_label
                        ? ` · ${selectedFlight.offer.baggage_label}`
                        : ''}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500">PNR Kodu</div>
                    <div className="text-2xl font-bold text-sky-600">{bookingResult.Pnr || bookingResult.pnr}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Sipariş No</div>
                    <div className="text-lg font-medium">{bookingResult.OrderID || bookingResult.booking_id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Durum</div>
                    <Badge variant="outline" className="text-green-600">
                      {bookingResult.PnrStatus || 'Rezervasyon Yapıldı'}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Toplam Tutar</div>
                    <FlightPrice
                      amountEur={bookingResult.total_with_fee ?? selectedFlight.offer.total_price}
                      size="default"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 justify-center">
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/${i18n.language}/flights`)}
                >
                  Yeni Arama
                </Button>
                <Button onClick={() => window.print()}>
                  <FileText className="w-4 h-4 mr-2" />
                  Yazdır
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  const { offer, flight } = selectedFlight;
  const displayBase = pricedQuote?.base_price ?? offer.base_price;
  const displayHandling = Number(pricedQuote?.handling_fee ?? 0);
  const displayTotal = pricedQuote?.total_with_fee ?? offer.total_price;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white py-8">
      <div className="container mx-auto px-4">
        {paymentNotice?.type === 'failed' && (
          <div
            className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-900"
            role="alert"
            data-testid="flight-payment-failed-banner"
          >
            <AlertCircle className="w-4 h-4 shrink-0 text-red-600 mt-0.5" />
            <div className="min-w-0">
              <p className="font-medium leading-snug">
                Ödeme başarısız
                {paymentNotice.orderId ? (
                  <span className="font-normal text-red-700"> · {paymentNotice.orderId}</span>
                ) : null}
              </p>
              <p className="text-xs text-red-800 mt-0.5 leading-relaxed">{paymentNotice.message}</p>
              <p className="text-xs text-red-600/90 mt-1">Bilet kesilmedi, PNR oluşmadı.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Flight Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plane className="w-5 h-5" />
                  Uçuş Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-sky-100 rounded-lg flex items-center justify-center">
                    <span className="text-xl font-bold text-sky-600">
                      {flight?.airline_code || 'XX'}
                    </span>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="text-2xl font-bold">{formatTime(flight?.departure_time)}</div>
                        <div className="text-sm text-gray-500">
                          {flight?.segments?.[0]?.DepartureAirport || flight?.departure_airport}
                        </div>
                      </div>
                      
                      <div className="flex-1 flex items-center justify-center">
                        <div className="flex items-center gap-2">
                          <div className="h-px w-12 bg-gray-300" />
                          <Plane className="w-4 h-4 text-sky-600" />
                          <div className="h-px w-12 bg-gray-300" />
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold">{formatTime(flight?.arrival_time)}</div>
                        <div className="text-sm text-gray-500">
                          {flight?.segments?.[flight?.segments?.length - 1]?.ArrivalAirport || flight?.arrival_airport}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span className="font-medium text-gray-800">
                        {selectedFlight?.offer?.brand_name || flight?.brand_name || 'Economy'}
                      </span>
                      <span>•</span>
                      <span>{formatFlightConnectionLabel(flight)}</span>
                      {(selectedFlight?.offer?.baggage_label || flight?.baggage_label) && (
                        <>
                          <span>•</span>
                          <span>{selectedFlight?.offer?.baggage_label || flight?.baggage_label}</span>
                        </>
                      )}
                    </div>
                    {(selectedFlight?.offer?.features?.length > 0) && (
                      <ul className="mt-3 space-y-1">
                        {selectedFlight.offer.features.slice(0, 5).map((feat) => (
                          <li key={feat} className="text-xs text-gray-600">• {feat}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  İletişim Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Ad *</Label>
                    <Input
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      placeholder="Ad"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label>Soyad *</Label>
                    <Input
                      value={contact.surname}
                      onChange={(e) => setContact({ ...contact, surname: e.target.value })}
                      placeholder="Soyad"
                      data-testid="contact-surname"
                    />
                  </div>
                  <div>
                    <Label>E-posta *</Label>
                    <Input
                      type="email"
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      placeholder="ornek@email.com"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <Label>Telefon *</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={contact.phone_country} 
                        onValueChange={(v) => setContact({ ...contact, phone_country: v })}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+90">+90</SelectItem>
                          <SelectItem value="+49">+49</SelectItem>
                          <SelectItem value="+43">+43</SelectItem>
                          <SelectItem value="+41">+41</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={contact.phone}
                        onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                        placeholder="5551234567"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Payment — Ziraat 3D Secure */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Ödeme Bilgileri
                  <Badge variant="outline" className="ml-2">
                    <Lock className="w-3 h-3 mr-1" />
                    3D Secure
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  {paymentMode === 'ziraat'
                    ? 'Ödeme Ziraat Bankası Sanal POS üzerinden 3D Secure ile güvenli şekilde alınır. Onay e-postası Turkish E-Market tarafından gönderilir.'
                    : 'Uçuş bileti Airtuerk üzerinden kredi kartı ile tahsil edilir.'}
                </p>
                <div className="mb-4 pb-4 border-b">
                  <PaymentBrandLogos
                    bankLabel={paymentMode === 'ziraat' ? 'Ziraat Bankası Sanal POS' : undefined}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Kart numarası *</Label>
                    <Input
                      value={paymentCard.card_number}
                      onChange={(e) => handlePaymentCardChange('card_number', e.target.value)}
                      placeholder="1234 5678 9012 3456"
                      inputMode="numeric"
                      autoComplete="cc-number"
                      maxLength={19}
                      data-testid="payment-card-number"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Kart üzerindeki isim *</Label>
                    <Input
                      value={paymentCard.card_holder}
                      onChange={(e) => handlePaymentCardChange('card_holder', e.target.value.toUpperCase())}
                      placeholder="AD SOYAD"
                      data-testid="payment-card-name"
                    />
                  </div>
                  <div>
                    <Label>Son kullanma ay *</Label>
                    <Select
                      value={paymentCard.expire_month}
                      onValueChange={(v) => handlePaymentCardChange('expire_month', v)}
                    >
                      <SelectTrigger data-testid="payment-card-exp-month">
                        <SelectValue placeholder="AA" />
                      </SelectTrigger>
                      <SelectContent>
                        {cardMonthOptions.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Son kullanma yıl *</Label>
                    <Select
                      value={paymentCard.expire_year}
                      onValueChange={(v) => handlePaymentCardChange('expire_year', v)}
                    >
                      <SelectTrigger data-testid="payment-card-exp-year">
                        <SelectValue placeholder="YY" />
                      </SelectTrigger>
                      <SelectContent>
                        {cardYearOptions.map((y) => (
                          <SelectItem key={y} value={y}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>CVC / CVV *</Label>
                    <Input
                      type="password"
                      value={paymentCard.cvc}
                      onChange={(e) => handlePaymentCardChange('cvc', e.target.value)}
                      placeholder="***"
                      maxLength={4}
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      data-testid="payment-card-cvc"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Passenger Information */}
            {passengers.map((pax, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {index + 1}. Yolcu 
                    <Badge variant="outline" className="ml-2">
                      {pax.pax_type === 'ADULT' ? 'Yetişkin' : pax.pax_type === 'CHILD' ? 'Çocuk' : 'Bebek'}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label>Cinsiyet *</Label>
                      <Select 
                        value={pax.gender} 
                        onValueChange={(v) => updatePassenger(index, 'gender', v)}
                      >
                        <SelectTrigger data-testid={`pax-${index}-gender`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MALE">Erkek</SelectItem>
                          <SelectItem value="FEMALE">Kadın</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Ad *</Label>
                      <Input
                        value={pax.name}
                        onChange={(e) => updatePassenger(index, 'name', e.target.value)}
                        placeholder="Ad (Pasaporttaki gibi)"
                        data-testid={`pax-${index}-name`}
                      />
                    </div>
                    <div>
                      <Label>Soyad *</Label>
                      <Input
                        value={pax.surname}
                        onChange={(e) => updatePassenger(index, 'surname', e.target.value)}
                        placeholder="Soyad (Pasaporttaki gibi)"
                        data-testid={`pax-${index}-surname`}
                      />
                    </div>
                    <div>
                      <Label>Doğum Tarihi *</Label>
                      <Input
                        type="date"
                        value={pax.birthdate}
                        onChange={(e) => updatePassenger(index, 'birthdate', e.target.value)}
                        data-testid={`pax-${index}-birthdate`}
                      />
                    </div>
                    <div>
                      <Label>Uyruk</Label>
                      <Select 
                        value={pax.nationality} 
                        onValueChange={(v) => updatePassenger(index, 'nationality', v)}
                      >
                        <SelectTrigger data-testid={`pax-${index}-nationality`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TR">Türkiye</SelectItem>
                          <SelectItem value="DE">Almanya</SelectItem>
                          <SelectItem value="AT">Avusturya</SelectItem>
                          <SelectItem value="CH">İsviçre</SelectItem>
                          <SelectItem value="NL">Hollanda</SelectItem>
                          <SelectItem value="GB">İngiltere</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>TC Kimlik No</Label>
                      <Input
                        value={pax.identity_number}
                        onChange={(e) => updatePassenger(index, 'identity_number', e.target.value)}
                        placeholder="11 haneli TC Kimlik No"
                        maxLength={11}
                        data-testid={`pax-${index}-tc`}
                      />
                    </div>
                    <div>
                      <Label>Pasaport No</Label>
                      <Input
                        value={pax.passport_number}
                        onChange={(e) => updatePassenger(index, 'passport_number', e.target.value)}
                        placeholder="Pasaport numarası"
                        data-testid={`pax-${index}-passport`}
                      />
                    </div>
                    <div>
                      <Label>Pasaport Geçerlilik</Label>
                      <Input
                        type="date"
                        value={pax.passport_expire_date}
                        onChange={(e) => updatePassenger(index, 'passport_expire_date', e.target.value)}
                        data-testid={`pax-${index}-passport-expire`}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Price Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Fiyat Özeti</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Base price breakdown */}
                  {offer.ptc_breakdown?.map((ptc, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>
                        {ptc.pax_type === 'ADULT' ? 'Yetişkin' : ptc.pax_type === 'CHILD' ? 'Çocuk' : 'Bebek'} x1
                      </span>
                      <FlightPriceInline amountEur={ptc.total} />
                    </div>
                  ))}
                  
                  <Separator />
                  
                  <div className="flex justify-between text-sm">
                    <span>Bilet Ücreti</span>
                    <FlightPriceInline amountEur={displayBase} loading={priceLoading} />
                  </div>
                  
                  {displayHandling > 0 && (
                    <div className="flex justify-between text-sm text-amber-600">
                      <span>Handling Fee</span>
                      <FlightPriceInline amountEur={displayHandling} loading={priceLoading} />
                    </div>
                  )}
                  
                  <Separator />
                  
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Toplam</span>
                    <FlightPrice
                      amountEur={displayTotal}
                      size="default"
                      loading={priceLoading}
                      className="text-right"
                    />
                  </div>
                  
                  <p className="text-xs text-gray-500">
                    Uçak biletlerine KDV uygulanmaz.
                  </p>
                </div>
                
                {bookingError && !paymentNotice && (
                  <div
                    id="flight-booking-error"
                    className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-950"
                    role="alert"
                  >
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
                      <div className="space-y-1 min-w-0">
                        <p className="font-semibold text-red-900">Rezervasyon tamamlanamadı</p>
                        <p className="whitespace-pre-wrap leading-relaxed">{bookingError}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <FlightCancellationPolicy
                    airlineCode={flight?.airline_code}
                    compact
                  />
                </div>

                <Button 
                  className="w-full mt-6 py-6 text-lg bg-sky-600 hover:bg-sky-700"
                  onClick={handleBooking}
                  disabled={loading}
                  data-testid="complete-booking-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      İşleniyor...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Rezervasyonu Tamamla
                    </>
                  )}
                </Button>
                
                <div className="mt-4 p-3 bg-sky-50 rounded-lg border border-sky-100">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-sky-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-sky-800">
                      {paymentMode === 'ziraat'
                        ? 'Ödeme onaylandıktan sonra PNR kodunuz e-posta ile gönderilir ve başarı sayfasında görüntülenir.'
                        : 'Ödeme anında tahsil edilir. Başarılı işlemde PNR ekranda görünür; onay e-postası gönderilir.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightBookingPage;
